/**
 * BTW-identificatienummer normalisation — the frontend half of
 * `nl.scrivio.shared.domain.BtwNumber`.
 *
 * A Dutch BTW-identificatienummer is `NL`, nine digits, `B` and two more digits —
 * `NL123456789B01`. It is printed on the invoice Scrivio sends the practice (#4448), so
 * there is one canonical form and it is the only one the backend stores.
 *
 * Like its Kotlin counterpart this **normalises** rather than plainly refusing: a
 * praktijkhouder copying the number off a Belastingdienst letter types `NL 123456789 B01`,
 * and `nl123456789b01` from a keyboard that never shifted. Those are the same number.
 * Only whitespace and case are normalised — dots and hyphens are not stripped, because
 * they are not how a BTW-identificatienummer is written and accepting a shape nobody uses
 * would let a value of a different kind through looking well-formed.
 *
 * Asking here rather than letting the API answer is the point: the backend's refusal is a
 * 400 carrying an English sentence, and this is the inline message a practice can act on.
 * Reach the format through this module, never a regex of its own — the same rule
 * `billing:agb-format` keeps for {@link isValidAgbCode} (#4275).
 */
const BTW_NUMBER_PATTERN = /^NL\d{9}B\d{2}$/

const WHITESPACE = /\s/g

/**
 * The canonical form of `value` — uppercased with whitespace removed — or `null` when it is
 * absent or blank, which is the practice not having given one.
 *
 * A malformed value normalises to `null` as well: `null` means "nothing to store", and what
 * separates the two cases is {@link isValidBtwNumber}, which is what refuses the submit.
 */
export function normalizedBtwNumber(value: string | null | undefined): string | null {
  const candidate = value?.replace(WHITESPACE, '').toUpperCase()
  if (!candidate) return null
  return BTW_NUMBER_PATTERN.test(candidate) ? candidate : null
}

/**
 * @returns true if the value is empty or is a BTW-identificatienummer in any of the
 * spellings {@link normalizedBtwNumber} accepts. Empty input is "not provided", which the
 * field allows — nullability is a separate question.
 */
export function isValidBtwNumber(value: string | null | undefined): boolean {
  const trimmed = value?.trim()
  if (!trimmed) return true
  return normalizedBtwNumber(trimmed) !== null
}
